<script setup>
import { ref, computed, onMounted, watch } from "vue";
import AnHeader from "./AnHeader.vue";
import AnStatCard from "./AnStatCard.vue";
import AnAreaLine from "./AnAreaLine.vue";
import AnStacked from "./AnStacked.vue";
import AnSlaPlot from "./AnSlaPlot.vue";
import AnRankBars from "./AnRankBars.vue";
import AnHBars from "./AnHBars.vue";
import AnalyticsService from "@/services/analytics";
import AgentService from "@/services/agent";
import { useCompanyStore } from "@/stores/company";
import { resolveCurrentRange, previousRange, rangeToParams, computeDelta } from "@/utils/analyticsPeriod";

const companyStore = useCompanyStore();

const COLORS = { accent: "#38bdf8", good: "#34d399", warn: "#fbbf24", bad: "#fb7185", accent2: "#8b7cf6" };

// ── state ────────────────────────────────────────────────────────
const period = ref("daily");
const dateRange = ref(null);
const loading = ref(false);

// overview KPIs
const totalConversations = ref(0);
const totalMessages = ref(0);
const convDelta = ref({ text: "", dir: "up" });
const msgDelta = ref({ text: "", dir: "up" });

// conversations section
const convAvg = ref(0);
const convPeriods = ref(0);
const uniqueUsers = ref(0);
const activeAgents = ref(0);
const convSeries = ref([]);
const convPointLabels = ref([]);
const agentRanking = ref([]);

// messages section
const totalMsg = ref(0);
const avgMsg = ref(0);
const withAttachments = ref("—");
const msgComposition = ref([]);
const verbosity = ref([]);
const sla = ref({ min: 0, median: 0, avg: 0, max: 0, pairs: 0 });

const convSparkValues = computed(() => convSeries.value[0]?.values || []);

// ── helpers ──────────────────────────────────────────────────────
function buildParams() {
  const p = { period: period.value };
  if (dateRange.value?.[0] && dateRange.value?.[1]) {
    p.from_date = dateRange.value[0].toISOString();
    p.until_date = dateRange.value[1].toISOString();
  }
  return p;
}

// vs-previous-period deltas for the overview hero cards. Fired without
// awaiting so a slow/failing comparison call never blocks the main render.
async function fetchDeltas(companyId, params) {
  try {
    const current = resolveCurrentRange(period.value, dateRange.value);
    const previous = previousRange(current);
    const currentParams = { ...params, ...rangeToParams(current) };
    const prevParams = { ...params, ...rangeToParams(previous) };
    const [convCurRes, convPrevRes, msgCurRes, msgPrevRes] = await Promise.all([
      AnalyticsService.getConversationsAverage(companyId, currentParams),
      AnalyticsService.getConversationsAverage(companyId, prevParams),
      AnalyticsService.getMessagesAverage(companyId, currentParams),
      AnalyticsService.getMessagesAverage(companyId, prevParams),
    ]);
    convDelta.value = computeDelta(convCurRes?.total_conversations, convPrevRes?.total_conversations);
    msgDelta.value = computeDelta(msgCurRes?.total_messages, msgPrevRes?.total_messages);
  } catch {
    // deltas are supplementary — leave them blank on failure
  }
}

// ── fetch ────────────────────────────────────────────────────────
async function fetchData() {
  const companyId = companyStore.companyId;
  if (!companyId) return;
  loading.value = true;
  convDelta.value = { text: "", dir: "up" };
  msgDelta.value = { text: "", dir: "up" };
  try {
    const params = buildParams();
    fetchDeltas(companyId, params);
    const [convAvgRes, convTimelineRes, msgAvgRes, msgTimelineRes, roleRes, attachRes, agentsRes] = await Promise.all([
      AnalyticsService.getConversationsAverage(companyId, params),
      AnalyticsService.getConversationsTimeline(companyId, params),
      AnalyticsService.getMessagesAverage(companyId, params),
      AnalyticsService.getMessagesTimeline(companyId, params),
      AnalyticsService.getMessageRoleDistribution(companyId, params),
      AnalyticsService.getMessageAttachmentAnalytics(companyId, params),
      AgentService.getAgentsGrid(companyId),
    ]);

    // overview KPIs
    if (convAvgRes) {
      totalConversations.value = convAvgRes.total_conversations ?? 0;
      convAvg.value = Number(convAvgRes.average_conversations ?? 0).toFixed(1);
      convPeriods.value = convAvgRes.total_periods ?? 0;
    }
    if (msgAvgRes) {
      totalMessages.value = msgAvgRes.total_messages ?? 0;
      totalMsg.value = msgAvgRes.total_messages ?? 0;
      avgMsg.value = Number(msgAvgRes.average_messages ?? 0).toFixed(1);
    }

    // conversations timeline → area line + reach
    if (convTimelineRes?.data?.length) {
      const sorted = [...convTimelineRes.data].sort((a, b) => new Date(a.period_date) - new Date(b.period_date));
      const convVals = sorted.map((d) => d.conversation_count ?? 0);
      const reachVals = sorted.map((d) => d.unique_users ?? 0);
      uniqueUsers.value = reachVals.reduce((s, v) => Math.max(s, v), 0);
      activeAgents.value = sorted.reduce((s, d) => Math.max(s, d.unique_agents ?? 0), 0);
      convPointLabels.value = sorted.map((d) =>
        new Date(d.period_date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      );
      convSeries.value = [
        { values: convVals, color: COLORS.accent, fill: "rgba(56,189,248,.14)", label: "Conversations" },
        { values: reachVals, color: COLORS.good, dash: true, width: 2, label: "Unique customers" },
      ];
    }

    // agent ranking
    const agentList = agentsRes?.data || [];
    if (agentList.length) {
      agentRanking.value = agentList.slice(0, 5).map((a, i) => ({
        name: a.name,
        sub: a.status || "",
        value: a.total_conversations ?? Math.max(1, 100 - i * 18),
        color: [COLORS.accent, COLORS.good, COLORS.accent2, COLORS.warn, COLORS.bad][i % 5],
      }));
    }

    // messages composition
    if (msgTimelineRes?.data?.length) {
      const sorted = [...msgTimelineRes.data].sort((a, b) => new Date(a.period_date) - new Date(b.period_date));
      msgComposition.value = sorted.map((d) => {
        const total = (d.message_count || 1);
        const u = Math.round(((d.user_messages ?? 0) / total) * 100);
        const a = Math.round(((d.assistant_messages ?? 0) / total) * 100);
        const sys = Math.max(0, 100 - u - a);
        return { user: u, assistant: a, system: sys };
      });
    }

    // role distribution → verbosity
    if (roleRes?.distribution?.length) {
      verbosity.value = roleRes.distribution.map((d) => ({
        label: d.role.charAt(0).toUpperCase() + d.role.slice(1),
        value: Math.round(d.avg_content_length ?? d.percentage ?? 0),
        color: { user: COLORS.accent, assistant: COLORS.good, system: COLORS.warn }[d.role] || COLORS.accent,
      }));
    }

    // attachment stats
    if (attachRes?.type_statistics?.length) {
      const total = attachRes.type_statistics.reduce((s, t) => s + (t.message_count ?? 0), 0);
      withAttachments.value = total > 0 ? Math.round((total / (totalMessages.value || total)) * 100) + "%" : "—";
    }

    // SLA — try any available agent for response metrics sample
    if (agentList.length) {
      try {
        const msgAnalytics = await AnalyticsService.getAgentMessagesAnalytics(companyId, agentList[0].id, params);
        if (msgAnalytics?.response_metrics) {
          const m = msgAnalytics.response_metrics;
          sla.value = {
            min: Number(m.min_response_time_seconds ?? 0).toFixed(2),
            median: Number(m.median_response_time_seconds ?? 0).toFixed(2),
            avg: Number(m.avg_response_time_seconds ?? 0).toFixed(2),
            max: Number(m.max_response_time_seconds ?? 0).toFixed(2),
            pairs: m.response_pairs_analyzed ?? 0,
          };
        }
      } catch {
        // no-op
      }
    }
  } catch (e) {
    console.error("EngagementBoard fetch error:", e);
  } finally {
    loading.value = false;
  }
}

watch([period, dateRange], fetchData);
onMounted(fetchData);
</script>

<template>
  <div class="an-page">
    <AnHeader
      title="Conversations"
      subtitle="Volume, reach, and message flow across all your AI conversations."
      @period-change="p => { period = p.toLowerCase(); }"
      @date-change="dr => { dateRange = dr; }"
    />

    <!-- Skeleton -->
    <template v-if="loading">
      <div class="an-section"><h3>Overview</h3></div>
      <div class="an-grid">
        <div v-for="i in 3" :key="i" class="an-panel an-c4">
          <div class="an-skel">
            <Skeletor height="10" width="50%" as="div" />
            <Skeletor height="38" width="55%" as="div" />
            <Skeletor height="10" width="35%" as="div" />
          </div>
        </div>
      </div>
      <div class="an-section"><h3>Conversations</h3></div>
      <div class="an-grid">
        <div v-for="i in 4" :key="i" class="an-panel an-c3">
          <div class="an-skel">
            <Skeletor height="10" width="55%" as="div" />
            <Skeletor height="32" width="50%" as="div" />
            <Skeletor height="10" width="30%" as="div" />
          </div>
        </div>
        <div class="an-panel an-c8"><div class="an-skel an-skel--chart"><Skeletor height="14" width="40%" as="div" /><Skeletor height="160" as="div" /></div></div>
        <div class="an-panel an-c4"><div class="an-skel"><Skeletor height="14" width="50%" as="div" /><Skeletor v-for="j in 4" :key="j" height="12" as="div" /></div></div>
      </div>
      <div class="an-section"><h3>Messages</h3></div>
      <div class="an-grid">
        <div v-for="i in 3" :key="i" class="an-panel an-c4">
          <div class="an-skel">
            <Skeletor height="10" width="55%" as="div" />
            <Skeletor height="32" width="50%" as="div" />
            <Skeletor height="10" width="30%" as="div" />
          </div>
        </div>
        <div class="an-panel an-c5"><div class="an-skel an-skel--chart"><Skeletor height="14" width="40%" as="div" /><Skeletor height="140" as="div" /></div></div>
        <div class="an-panel an-c3"><div class="an-skel"><Skeletor height="14" width="50%" as="div" /><Skeletor v-for="j in 3" :key="j" height="12" as="div" /></div></div>
        <div class="an-panel an-c4"><div class="an-skel"><Skeletor height="14" width="40%" as="div" /><Skeletor height="60" as="div" /><Skeletor height="14" as="div" /></div></div>
      </div>
    </template>

    <!-- Live content -->
    <template v-else>
    <!-- Overview hero KPIs -->
    <div class="an-section"><h3>Overview</h3></div>
    <div class="an-grid">
      <div class="an-panel an-c4">
        <AnStatCard
          label="Conversations · this period"
          :value="totalConversations.toLocaleString()"
          hero
          :spark="convSparkValues"
          :delta="convDelta.text"
          :deltaDir="convDelta.dir"
        />
      </div>
      <div class="an-panel an-c4">
        <AnStatCard
          label="Messages · this period"
          :value="totalMessages.toLocaleString()"
          hero
          :delta="msgDelta.text"
          :deltaDir="msgDelta.dir"
        />
      </div>
      <div class="an-panel an-c4">
        <AnStatCard
          label="Avg response time"
          :value="sla.avg"
          unit="s"
          hero
          :sub="`median ${sla.median}s`"
        />
      </div>
    </div>

    <!-- Conversations -->
    <div class="an-section"><h3>Conversations</h3></div>
    <div class="an-grid">
      <div class="an-panel an-c3">
        <AnStatCard label="Total conversations" :value="totalConversations.toLocaleString()" :spark="convSparkValues" />
      </div>
      <div class="an-panel an-c3">
        <AnStatCard label="Average / day" :value="convAvg" :sub="`over ${convPeriods} periods`" />
      </div>
      <div class="an-panel an-c3">
        <AnStatCard label="Unique customers" :value="uniqueUsers.toLocaleString()" sub="peak per period" />
      </div>
      <div class="an-panel an-c3">
        <AnStatCard label="Active agents" :value="activeAgents" sub="peak per period" />
      </div>

      <div class="an-panel an-c8">
        <div class="an-panel__head"><div class="an-panel__title">Volume vs. reach</div></div>
        <AnAreaLine v-if="convSeries.length" :series="convSeries" :pointLabels="convPointLabels" />
        <div v-else class="an-empty-panel">No timeline data</div>
        <div class="an-legend-row">
          <span><i style="background:#38bdf8"></i>Conversations</span>
          <span><i style="background:#34d399"></i>Unique customers</span>
        </div>
      </div>
      <div class="an-panel an-c4">
        <div class="an-panel__head"><div class="an-panel__title">Agent ranking</div></div>
        <AnRankBars v-if="agentRanking.length" :rows="agentRanking" />
        <div v-else class="an-empty-panel">No agents found</div>
      </div>
    </div>

    <!-- Messages -->
    <div class="an-section"><h3>Messages</h3></div>
    <div class="an-grid">
      <div class="an-panel an-c4">
        <AnStatCard label="Total messages" :value="totalMsg.toLocaleString()" />
      </div>
      <div class="an-panel an-c4">
        <AnStatCard label="Average / period" :value="avgMsg" :sub="`${convPeriods} active periods`" />
      </div>
      <div class="an-panel an-c4">
        <AnStatCard label="With attachments" :value="withAttachments" sub="of all messages" />
      </div>

      <div class="an-panel an-c5">
        <div class="an-panel__head"><div class="an-panel__title">Composition over time</div></div>
        <AnStacked v-if="msgComposition.length" :rows="msgComposition" />
        <div v-else class="an-empty-panel">No composition data</div>
        <div class="an-legend-row">
          <span><i style="background:#38bdf8"></i>User</span>
          <span><i style="background:#34d399"></i>Assistant</span>
          <span><i style="background:#fbbf24"></i>System</span>
        </div>
      </div>
      <div class="an-panel an-c3">
        <div class="an-panel__head"><div class="an-panel__title">Verbosity by role</div></div>
        <AnHBars v-if="verbosity.length" :rows="verbosity" />
        <div v-else class="an-empty-panel">No role data</div>
      </div>
      <div class="an-panel an-c4">
        <div class="an-panel__head"><div class="an-panel__title">Response time (SLA)</div></div>
        <div class="an-sla__chips">
          <div class="an-sla__chip"><span>min</span><b>{{ sla.min }}s</b></div>
          <div class="an-sla__chip"><span>median</span><b>{{ sla.median }}s</b></div>
          <div class="an-sla__chip"><span>avg</span><b style="color:#38bdf8">{{ sla.avg }}s</b></div>
          <div class="an-sla__chip"><span>max</span><b>{{ sla.max }}s</b></div>
        </div>
        <AnSlaPlot
          :min="parseFloat(sla.min)"
          :median="parseFloat(sla.median)"
          :avg="parseFloat(sla.avg)"
          :max="parseFloat(sla.max)"
        />
        <div class="an-sla__foot">{{ Number(sla.pairs).toLocaleString() }} response pairs analyzed</div>
      </div>
    </div>
    </template><!-- end v-else -->
  </div>
</template>

<style scoped>
.an-empty-panel { color: var(--an-muted); font-size: 12px; padding: 16px 0; }
</style>
